// Downsize a picked image in the browser before upload (IMPLEMENTATION_PLAN §3.4 R2): longest side ≤ 1600 px,
// re-encoded as JPEG 0.8. EXIF orientation is applied while decoding, so portrait phone photos stay upright;
// the output carries no EXIF (no GPS position leaks from the photo).
import { fitWithin, PHOTO_JPEG_QUALITY, PHOTO_MAX_SIDE } from '@fleetspoke/core/photos';

export class ImageDecodeError extends Error {}

export async function downscaleImage(
  file: Blob,
  maxSide: number = PHOTO_MAX_SIDE,
  quality: number = PHOTO_JPEG_QUALITY,
): Promise<Blob> {
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    throw new ImageDecodeError('not an image this browser can read');
  }
  const { width, height } = fitWithin(bitmap.width, bitmap.height, maxSide);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new ImageDecodeError('no canvas');
  ctx.fillStyle = '#FFFFFF'; // transparent PNGs become white, not black, as JPEG
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', quality),
  );
  if (!blob) throw new ImageDecodeError('could not encode the photo');
  return blob;
}
